import { useMemo, useState, useEffect } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import {
  getCurrencies,
  getRecentCurrencies,
  pushRecentCurrency,
  formatCurrencyPreview,
} from "../utils/currencyList";

function norm(s) {
  return (s || "").toString().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

export default function BaseCurrencySelect({
  value,                // ISO code, e.g. 'EUR'
  onChange,             // (code) => void
  label = "Base Currency",
  placeholder = "Search currency…",
  disabled = false,
  className = "",
}) {
  const [query, setQuery] = useState("");
  const all = useMemo(() => getCurrencies(), []);
  const selected = useMemo(
    () => all.find((c) => c.code === String(value || "").toUpperCase()) || null,
    [value, all]
  );

  const filtered = useMemo(() => {
    const q = norm(query);
    if (!q) return all;
    return all.filter((c) => c.normKey.includes(q));
  }, [query, all]);

  // recent list, refresh when the menu opens the first time
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    setRecent(getRecentCurrencies());
  }, []);

  const select = (item) => {
    if (!item) return;
    pushRecentCurrency(item.code);
    onChange?.(item.code);
  };

  return (
    <div className={`w-full ${className}`}>
      {label ? (
        <label className="text-sm font-medium text-gray-800 mb-1 block">{label}</label>
      ) : null}

      <Combobox value={selected} onChange={select} disabled={disabled} nullable>
        <div className="relative">
          {/* Button / Input */}
          <div className="flex items-center gap-2 input rounded-xl border border-gray-300 bg-white">
            <Combobox.Input
              displayValue={(item) => item ? `${item.code} — ${item.name}` : ""}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="w-full outline-none text-sm bg-transparent py-2"
              aria-label={label}
            />
            {!!query && (
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setQuery("")}
                aria-label="Clear"
              >
                <X size={16} />
              </button>
            )}
            <Combobox.Button className="text-gray-500">
              <ChevronDown size={16} />
            </Combobox.Button>
          </div>

          {/* Options */}
          <Transition
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Combobox.Options className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-xl border bg-white shadow-lg focus:outline-none">
              {/* Recent (only when no query and we have recents) */}
              {!query && recent.length > 0 && (
                <>
                  <div className="px-3 pt-2 pb-1 text-[11px] text-gray-500">Recent</div>
                  {recent.map((c) => (
                    <OptionRow key={`r-${c.code}`} item={c} />
                  ))}
                  <div className="my-1 border-t" />
                </>
              )}

              {/* Main list (filtered) */}
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No matches.</div>
              ) : (
                filtered.map((c) => <OptionRow key={c.code} item={c} />)
              )}
            </Combobox.Options>
          </Transition>
        </div>

      </Combobox>

      {/* Tiny helper preview under the field */}
      {selected ? (
        <p className="mt-1 text-[11px] text-gray-500">
          Example: <span className="font-medium">{formatCurrencyPreview(selected.code)}</span> ·{" "}
          {selected.digits} decimal{selected.digits === 1 ? "" : "s"}
        </p>
      ) : null}
    </div>
  );
}

function OptionRow({ item }) {
  return (
    <Combobox.Option
      value={item}
      className={({ active }) =>
        `flex items-center justify-between px-3 py-2 text-sm cursor-pointer ${
          active ? "bg-indigo-50" : ""
        }`
      }
    >
      {({ selected, active }) => (
        <>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="shrink-0 tabular-nums">{item.symbol}</span>
              <span className="font-medium">{item.code}</span>
              <span className="text-gray-500 truncate">— {item.name}</span>
            </div>
            {/* Countries are numerous; skip listing to avoid noise.
                If you want, add a tooltip/title with countries: */}
            {/* <div className="text-[11px] text-gray-500 truncate">{item.countries.join(", ")}</div> */}
          </div>
          {selected ? (
            <Check className={`h-4 w-4 ${active ? "text-indigo-600" : "text-gray-500"}`} />
          ) : null}
        </>
      )}
    </Combobox.Option>
  );
}
